import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { PrismicRichText } from '@prismicio/react';
import { FC } from 'react';
import { ArrowRightIcon } from '@/assets';
import { HeroCtaWithLogosSliceWithStats } from '../../../../prismicio-types';
import StatItem from './StatItem';

interface HeroCtaWithLogosSliceWithStatsProps {
  slice: HeroCtaWithLogosSliceWithStats;
}

// Type guard for stat items
function isStatItem(item: unknown): item is Record<string, unknown> {
  return typeof item === 'object' && item !== null;
}

const WithStats: FC<HeroCtaWithLogosSliceWithStatsProps> = (props) => {
  const { buttons, title, logo, description } = props.slice.primary;
  // Access stats with type guard since they may not be in generated types yet
  const statsField = (props.slice.primary as unknown as { stats?: unknown[] })
    .stats;
  const stats = Array.isArray(statsField) ? statsField : undefined;

  return (
    <div className="relative z-10 container mx-auto flex flex-col items-center text-center gap-8">
      <div className="container mx-auto px-4 sm:px-10 lg:px-[174px] py-10 lg:py-16 flex flex-col items-center text-center">
        <PrismicNextImage
          field={logo}
          className="w-[160px] object-contain mb-6"
          alt=""
        />
        <div className="text-white font-inter font-extrabold text-[56px] leading-[120%]">
          {title && (
            <PrismicRichText
              field={title}
              components={{
                heading1: ({ children }) => <div>{children}</div>,
                heading2: ({ children }) => <div>{children}</div>,
                paragraph: ({ children }) => <div>{children}</div>,
              }}
            />
          )}
        </div>

        {/* Description under title */}
        {description && (
          <div className="text-gray-300 text-lg mt-4 max-w-2xl">
            {Array.isArray(description) ? (
              <PrismicRichText
                field={
                  description as unknown as import('@prismicio/client').RichTextField
                }
                components={{
                  heading1: ({ children }) => <div>{children}</div>,
                  heading2: ({ children }) => <div>{children}</div>,
                  paragraph: ({ children }) => <div>{children}</div>,
                }}
              />
            ) : (
              <div>{description}</div>
            )}
          </div>
        )}

        {/* Stats (2-3 items) - inline layout */}
        {Array.isArray(stats) && stats.length > 0 && (
          <div className="mt-8 flex justify-center">
            <div className="flex flex-row items-center justify-center gap-6 flex-wrap">
              {stats.filter(isStatItem).map((item, idx) => (
                <StatItem key={idx} item={item} />
              ))}
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="flex flex-wrap justify-center gap-4 mt-8">
          {buttons
            ?.filter(
              (item) =>
                item.button_link &&
                item.button_link.link_type !== 'Any' &&
                item.button_text,
            )
            .map((item, index) => (
              <PrismicNextLink
                key={index}
                field={item.button_link}
                className={index === 0 ? 'secondary-button' : 'tertiary-button'}
              >
                <ArrowRightIcon className="arrow-icon" />
                {item.button_text}
              </PrismicNextLink>
            ))}
        </div>
      </div>
    </div>
  );
};

export default WithStats;
